import axios from 'axios'
import Cookies from 'js-cookie'

// Helper function to normalize base URL (remove quotes, trailing slashes, etc.)
const normalizeBaseURL = (url) => {
  if (!url) return '/api'
  
  // Remove quotes if present
  let normalized = String(url).trim().replace(/^['"]|['"]$/g, '')
  
  // Remove trailing slash
  normalized = normalized.replace(/\/$/, '')
  
  // If it's a relative path, ensure it starts with /
  if (!normalized.startsWith('http') && !normalized.startsWith('/')) {
    normalized = '/' + normalized
  }
  
  return normalized || '/api'
}

// Get normalized base URL from environment
const getBaseURL = () => {
  return normalizeBaseURL(import.meta.env.VITE_API_BASE_URL)
}

// Create axios instance
const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request interceptor - Add access token to requests
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get('access_token')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Don't try to refresh token for auth endpoints (login, forgot-password, reset-password)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                          originalRequest.url?.includes('/auth/forgot-password') ||
                          originalRequest.url?.includes('/auth/reset-password')

    // If error is 401 and we haven't tried to refresh yet (and it's not an auth endpoint)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      const refreshTokenValue = Cookies.get('refresh_token')

      if (refreshTokenValue) {
        try {
          // Try to refresh the token (use plain axios to avoid interceptor loop)
          const baseURL = getBaseURL()
          const response = await axios.post(
            `${baseURL}/auth/refresh`,
            { refresh_token: refreshTokenValue },
            { headers: { 'Content-Type': 'application/json' } }
          )

          if (response.data.success) {
            const { access_token, refresh_token } = response.data.data

            // Update cookies
            Cookies.set('access_token', access_token, { expires: 1 / 24 }) // 1 hour
            Cookies.set('refresh_token', refresh_token, { expires: 30 }) // 30 days

            // Note: Redux state will be updated automatically when the next API call succeeds
            // or when checkAuth runs. No need to dispatch here to avoid circular dependency.

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`
            return apiClient(originalRequest)
          }
        } catch (refreshError) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove('access_token')
          Cookies.remove('refresh_token')
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, clear cookies and redirect to login
        Cookies.remove('access_token')
        Cookies.remove('refresh_token')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Export the helper function for use in other files
export { getBaseURL, normalizeBaseURL }
export default apiClient

